<template>
  <div class="d-flex justify-content-between align-items-center">
    <div class="d-flex align-items-center" style="max-width: 70%;"> 
      <span class="me-2 text-truncate">{{ label }}</span>
    </div>
    <div
      class="smart-toggle"
      :class="[
        { disabled: isDisabled },
        variantClass
      ]"
    >
      <input
        type="checkbox"
        :id="computedId"
        :checked="booleanValue"
        :disabled="isDisabled"
        @change="onChange"
      />

      <label :for="computedId" class="toggle-label">
        <span class="toggle-inner">
          <span class="toggle-option on">{{ onText }}</span>
          <span class="toggle-option off">{{ offText }}</span>
        </span>
        <span class="toggle-handle"></span>
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type ToggleVariant = | 'success' | 'secondary' | 'danger' | 'warning'  | 'info'  | 'primary' 
interface Props {
  id: number | string
  modelValue: boolean |string |null | undefined
  label?: string
  onText?: string
  offText?: string
  color?: ToggleVariant
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  onText: 'Yes',
  offText: 'No',
  color: 'success'
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'toggle', value: boolean): void
}>()

const isDisabled = computed(() =>
  props.modelValue === null || props.modelValue === undefined
)

const booleanValue = computed<boolean>({
  get() {
    if (typeof props.modelValue === 'string') {
      return props.modelValue.toLowerCase() === 'true'
    }
    return Boolean(props.modelValue)
  },
  set(value: boolean) {
    emit('update:modelValue', value)
  }
})

const computedId = computed(() => `smartToggle_${props.id}`)
const variantClass = computed(() => `smart-toggle--${props.color}`)

const onChange = (event: Event): void => {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.checked)
  emit('toggle', target.checked)
}


</script>
<style scoped>
.smart-toggle input:checked + .toggle-label {
    background: var(--toggle-active-color);
}

.smart-toggle--primary { --toggle-active-color: #0d6efd; }
.smart-toggle--success { --toggle-active-color: #28a745; }
.smart-toggle--secondary { --toggle-active-color: #6c757d; }
.smart-toggle--danger { --toggle-active-color: #dc3545; }
.smart-toggle--warning { --toggle-active-color: #ffc107; }
.smart-toggle--info { --toggle-active-color: #0dcaf0; }


.smart-toggle input[type="checkbox"] {
    display: none;
}

.toggle-label {
    display: block;
    width: 60px;
    height: 30px;
    background: #e9ecef;
    border-radius: 15px;
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
}

.toggle-inner {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    font-size: 11px;
    font-weight: 600;
    color: #6c757d;
}

.toggle-handle {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 26px;
    height: 26px;
    background: white;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    transition: transform 0.3s ease;
}

/* Estado activado */
.smart-toggle input:checked + .toggle-label {   
    background: var(--toggle-active-color);
}

.smart-toggle input:checked + .toggle-label .toggle-handle {
    transform: translateX(30px);
}

.smart-toggle input:checked + .toggle-label .toggle-inner {
    color: white;
}

/* Versión pequeña */
.smart-toggle.small .toggle-label {
    width: 50px;
    height: 24px;
}

.smart-toggle.small .toggle-handle {
    width: 20px;
    height: 20px;
}

.smart-toggle.small input:checked + .toggle-label .toggle-handle {
    transform: translateX(26px);
}

/* Estado deshabilitado */
.smart-toggle.disabled .toggle-label {
    background: #adb5bd;
    cursor: not-allowed;
}
    
</style>